import React, { useState } from "react";
import { useGetPobImagesQuery } from "state/api";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Modal,
  Grid,
  Button,
  Chip,
  useMediaQuery,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import RingLoader from "react-spinners/RingLoader";
import { Autocomplete, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";

const PobImagePreviewModal = () => {
  const today = new Date();
  const [day, setDay] = useState(today.getDate());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  const {
    data: appdata,
    error,
    isLoading,
  } = useGetPobImagesQuery({ day, month, year });
  const [previewImage, setPreviewImage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedEmpNo, setSelectedEmpNo] = useState("");
  const navigate = useNavigate();
  const handlePreviewImage = (pic) => {
    setPreviewImage(pic);
    setIsFullscreen(false);
  };

  const handleDownload = (imageUrl) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "prescription.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add these variable declarations before return statement
  const sortedData = [...(appdata?.data || [])].sort(
    (a, b) => new Date(b.SUBDATE) - new Date(a.SUBDATE)
  );

  const uniqueEmpNos = [...new Set(sortedData.map((item) => item.EmpNo))];

  const filteredData =
    selectedEmpNo && selectedEmpNo !== "All"
      ? sortedData.filter((item) => item.EmpNo === selectedEmpNo)
      : sortedData;

  // ... existing handlers and logic ...

  const CustomSpinner = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#0f172a",
      }}
    >
      <RingLoader color="#3b82f6" size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;

  if (error)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 3,
          textAlign: "center",
          padding: 4,
          background: "#0f172a",
        }}
      >
        <Typography variant="h5" sx={{ color: "#ef4444" }}>
          Failed to Load Data
        </Typography>
        <Typography variant="body1" sx={{ color: "#94a3b8" }}>
          {error.message || "Unable to fetch prescription data"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{
            mt: 2,
            py: 1.5,
            px: 4,
            borderRadius: 2,
            fontWeight: "bold",
            textTransform: "none",
            background: "#3b82f6",
            color: "white",
            "&:hover": {
              background: "#2563eb",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 16px rgba(59, 130, 246, 0.3)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Try Again
        </Button>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#0f172a",
        padding: { xs: 2, md: 4 },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 3,
          mb: 4,
          padding: 3,
          borderRadius: 4,
          background: "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)",
          boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "white",
            flex: 1,
          }}
        >
          POB Prescriptions
        </Typography>

        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIosNew />}
          sx={{
            py: 1,
            px: 2.5,
            borderRadius: 2,
            fontWeight: "bold",
            textTransform: "none",
            background: "white",
            color: "#3b82f6",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
              background: "rgba(255,255,255,0.95)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Back
        </Button>
      </Box>

      {/* Filter Section */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          type="number"
          label="Day"
          value={day}
          onChange={(e) =>
            setDay(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))
          }
          InputProps={{ min: 1, max: 31 }}
          sx={{
            width: { xs: "100%", sm: 80 },
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.5)",
            },
            "& .MuiInputLabel-root": { color: "white" },
          }}
        />

        <TextField
          type="number"
          label="Month"
          value={month}
          onChange={(e) =>
            setMonth(Math.min(12, Math.max(1, parseInt(e.target.value) || 1)))
          }
          InputProps={{ min: 1, max: 12 }}
          sx={{
            width: { xs: "100%", sm: 90 },
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.5)",
            },
            "& .MuiInputLabel-root": { color: "white" },
          }}
        />

        <TextField
          type="number"
          label="Year"
          value={year}
          onChange={(e) =>
            setYear(parseInt(e.target.value) || new Date().getFullYear())
          }
          InputProps={{ min: 2020, max: 2100 }}
          sx={{
            width: { xs: "100%", sm: 100 },
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.5)",
            },
            "& .MuiInputLabel-root": { color: "white" },
          }}
        />

        <Autocomplete
          options={["All", ...uniqueEmpNos]}
          value={selectedEmpNo}
          onChange={(event, newValue) => setSelectedEmpNo(newValue || "All")}
          sx={{
            flex: 1,
            minWidth: { xs: "100%", sm: 200 },
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.5)",
            },
            "& .MuiInputLabel-root": { color: "white" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by Employee"
              variant="outlined"
            />
          )}
        />

        <Button
          onClick={() => {
            setDay(today.getDate());
            setMonth(today.getMonth() + 1);
            setYear(today.getFullYear());
            setSelectedEmpNo("All");
          }}
          variant="contained"
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: 2,
            fontWeight: "bold",
            textTransform: "none",
            background: "#ef4444",
            color: "white",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              background: "#dc2626",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 16px rgba(239, 68, 68, 0.3)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Reset Filters
        </Button>
      </Box>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  background: "rgba(30, 41, 59, 0.8)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 16px 32px rgba(59, 130, 246, 0.3)",
                    borderColor: "#3b82f6",
                  },
                }}
              >
                <Box sx={{ position: "relative", overflow: "hidden" }}>
                  <CardMedia
                    component="img"
                    image={item.pic}
                    alt={`Prescription for ${item.DoctName}`}
                    onClick={() => handlePreviewImage(item.pic)}
                    sx={{
                      height: 250,
                      cursor: "zoom-in",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                  <Chip
                    label={item.STATUS}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      background:
                        item.STATUS === "APPROVED"
                          ? "#10b981"
                          : item.STATUS === "REJECTED"
                          ? "#ef4444"
                          : "#f59e0b",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>

                <CardContent sx={{ bgcolor: "rgba(30, 41, 59, 0.5)" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: "white",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.NAME}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {[
                      { label: "Emp No", value: item.EmpNo },
                      { label: "Doctor", value: item.DoctName },
                      { label: "Specialty", value: item.DocCategory },
                      { label: "MCL Code", value: item.MclCode },
                      { label: "Strips", value: item.No_of_strips },
                      { label: "Chemist", value: item.Chemist_name },
                      { label: "DL No", value: item.DL_no },
                      {
                        label: "Submitted",
                        value: new Date(item.SUBDATE).toLocaleDateString(),
                      },
                      { label: "HQ", value: item.HeadQrt },
                    ].map((field, i) => (
                      <Grid item xs={6} key={i}>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          >
                            {field.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#e2e8f0",
                              wordBreak: "break-word",
                              mt: 0.5,
                            }}
                          >
                            {field.value || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              py: 10,
            }}
          >
            <Typography variant="h6" sx={{ color: "#94a3b8" }}>
              No prescriptions found
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Preview Modal */}
      <Modal open={!!previewImage} onClose={() => setPreviewImage(null)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.95)",
            backdropFilter: "blur(10px)",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
              color: "white",
              bgcolor: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.25)",
                borderColor: "rgba(255,255,255,0.5)",
              },
              transition: "all 0.3s ease",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={() => handleDownload(previewImage)}
            sx={{
              position: "absolute",
              top: 24,
              left: 24,
              color: "white",
              bgcolor: "rgba(59, 130, 246, 0.2)",
              border: "1px solid rgba(59, 130, 246, 0.5)",
              "&:hover": {
                bgcolor: "rgba(59, 130, 246, 0.4)",
                borderColor: "#3b82f6",
              },
              transition: "all 0.3s ease",
              zIndex: 10,
            }}
          >
            <DownloadIcon />
          </IconButton>

          <Box
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
              border: "1px solid rgba(59, 130, 246, 0.3)",
              boxShadow: "0 25px 50px rgba(59, 130, 246, 0.4)",
            }}
          >
            <img
              src={previewImage}
              alt="Prescription preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PobImagePreviewModal;
